import { LoadingState } from '../../../shared/ui/LoadingState.jsx';
import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { formatRaidHpEok, formatRaidHpInput, raidHpValueFromInput } from '../../../shared/domain/raidHpFormat.js';
import { ElementIcon } from '../../nikke-catalog/shared/ElementIcon.jsx';
import { RaidPracticeDeckDisplay, useSharedNikkeCatalog } from '../../raid-practice/shared/RaidPracticeDeckPicker.jsx';
import {
  buildGlobalTetrisPlan,
  buildTetrisCandidates,
  findBossTetrisCombinations,
  normalizeTetrisPracticeRecord,
} from '../domain/raidTetrisEngine.js';
import {
  readTetrisAvailabilityPreferences,
  writeTetrisAvailabilityPreference,
} from '../domain/raidTetrisAvailabilityPreferences.js';
import { useRaidTetris } from '../hooks/useRaidTetris.js';
import { RaidTetrisCandidateSelector } from './RaidTetrisCandidateSelector.jsx';
import { RaidTetrisFullSimulation } from './RaidTetrisFullSimulation.jsx';

function formatEok(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

function memberOpenid(value = {}) {
  return String(value.openid || value.memberOpenid || value.member_openid || '').trim();
}

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function targetStorageKey(unionId, seasonNo, phaseNo) {
  return `dororaid:tetris-targets:${String(unionId || 'union')}:${Number(seasonNo || 0)}:phase-${Number(phaseNo || 1)}`;
}

function candidateSelectionStorageKey(unionId, seasonNo) {
  return `dororaid:tetris-candidate-selection:${String(unionId || 'union')}:${Number(seasonNo || 0)}`;
}

function loadStoredCandidateSelection(key, validMemberKeys, validSlotIds) {
  if (typeof window === 'undefined') return { excludedMemberKeys: new Set(), excludedSlotIds: new Set() };
  try {
    const stored = JSON.parse(window.localStorage.getItem(key) || '{}');
    return {
      excludedMemberKeys: new Set((Array.isArray(stored.excludedMemberKeys) ? stored.excludedMemberKeys : []).filter((value) => validMemberKeys.has(value))),
      excludedSlotIds: new Set((Array.isArray(stored.excludedSlotIds) ? stored.excludedSlotIds : []).filter((value) => validSlotIds.has(value))),
    };
  } catch {
    return { excludedMemberKeys: new Set(), excludedSlotIds: new Set() };
  }
}

function defaultTargetMap(bosses = []) {
  return Object.fromEntries((Array.isArray(bosses) ? bosses : []).map((boss) => [
    Number(boss.bossNo),
    Math.max(0, numberValue(boss.remainingHpEok)),
  ]));
}

function loadStoredTargets(key, defaults) {
  if (typeof window === 'undefined') return defaults;
  try {
    const stored = JSON.parse(window.localStorage.getItem(key) || '{}');
    return Object.fromEntries(Object.entries(defaults).map(([bossNo, fallback]) => [
      bossNo,
      Math.max(0, numberValue(stored?.[bossNo], fallback)),
    ]));
  } catch {
    return defaults;
  }
}

function recommendationResult(recommendation = {}, resultIndex = 0) {
  if (recommendation.selected) return recommendation.selected;
  return recommendation.results?.[resultIndex]
    || recommendation.results?.[0]
    || recommendation.insufficient
    || null;
}

function recommendationLabel(recommendation, target) {
  if (!Number(target || 0)) return '목표 HP 없음';
  const result = recommendationResult(recommendation);
  if (!result?.items?.length) return '없음';
  const diff = numberValue(result.totalEok) - numberValue(target);
  return `${result.items.length}타 · ${formatEok(result.totalEok)} · ${diff >= 0 ? `초과 +${formatRaidHpEok(diff)}` : `부족 ${formatRaidHpEok(Math.abs(diff))}`}`;
}

function practiceSlotLabel(item = {}) {
  return String(item.title || '').trim() || `모의전 ${numberValue(item.slotNo, 1)}`;
}

function BossRecommendation({ recommendation, target }) {
  const result = recommendationResult(recommendation);
  if (!Number(target || 0)) return <div className="raid-tetris-matrix-rec empty">목표 HP 없음</div>;
  if (!result?.items?.length) return <div className="raid-tetris-matrix-rec empty">추천 가능한 조합 없음</div>;
  const diff = numberValue(result.totalEok) - numberValue(target);
  return (
    <div className={`raid-tetris-matrix-rec ${diff >= 0 ? 'enough' : 'lack'}`}>
      <div><span>추천 합계</span><strong>{formatEok(result.totalEok)}</strong></div>
      <p>{diff >= 0 ? `초과 +${formatRaidHpEok(diff)}` : `부족 ${formatRaidHpEok(Math.abs(diff))}`} · {result.items.length}타</p>
      <div className="raid-tetris-matrix-rec-members">
        {result.items.map((item) => <span key={item.id}>{item.nickname}</span>)}
      </div>
    </div>
  );
}

function RecommendationTabs({ recommendation, selectedIndex, onSelect }) {
  const results = Array.isArray(recommendation?.results) ? recommendation.results : [];
  if (results.length <= 1) return null;

  return (
    <div className="raid-tetris-alternative-tabs" role="tablist" aria-label="대체 추천 조합">
      {results.slice(0, 3).map((result, index) => {
        const bestDiff = Math.abs(numberValue(results[0]?.diffEok));
        const currentDiff = Math.abs(numberValue(result.diffEok));
        const difference = Math.max(0, currentDiff - bestDiff);
        return (
          <button
            className={selectedIndex === index ? 'active' : ''}
            type="button"
            role="tab"
            aria-selected={selectedIndex === index}
            key={result.items.map((item) => item.id).join('|') || index}
            onClick={() => onSelect(index)}
          >
            <strong>{index === 0 ? 'BEST' : `#${index + 1}`}</strong>
            <small>{index === 0 ? '기준 조합' : `BEST 대비 ${formatRaidHpEok(difference)}`}</small>
          </button>
        );
      })}
    </div>
  );
}

function RecommendationDecks({ result, target, mobile }) {
  if (!Number(target || 0)) return <div className="raid-tetris-recommend-empty">목표 HP를 입력해주세요.</div>;
  if (!result?.items?.length) return <div className="raid-tetris-recommend-empty">추천 가능한 모의전 조합 없음</div>;
  const diff = numberValue(result.totalEok) - numberValue(target);
  const memberCount = new Set(result.items.map((item) => item.memberOpenid)).size;
  return (
    <div className="raid-tetris-recommend-result">
      <div className={`raid-tetris-recommend-summary ${diff >= 0 ? 'enough' : 'lack'}`}>
        <div><small>합계</small><strong>{formatEok(result.totalEok)}</strong></div>
        <div><small>차이</small><strong>{diff >= 0 ? '+' : '-'}{formatRaidHpEok(Math.abs(diff))}</strong></div>
        <div><small>인원</small><strong>{memberCount}명 · {result.items.length}타</strong></div>
      </div>
      <div className="raid-tetris-recommend-decks">
        {result.items.map((item, index) => (
          <article key={item.id}>
            <header><span>{index + 1}타</span><div><strong>{item.nickname}</strong><small>{practiceSlotLabel(item)}</small></div><b>{formatEok(item.damageEok)}</b></header>
            <RaidPracticeDeckDisplay deck={item.deck} mobile={mobile} />
          </article>
        ))}
      </div>
    </div>
  );
}

function CompactMatrixCell({ records = [], selectedIds, availableIds, memberDone, mobile }) {
  if (memberDone) {
    return <div className="raid-tetris-compact-excluded"><strong>공격완료</strong><span>3회 완료 · 테트리스 제외</span></div>;
  }

  const selectedRecord = records.find((record) => selectedIds.has(record.id));
  const availableRecord = records.find((record) => availableIds.has(record.id));
  const usedRecord = records.find((record) => record.used);
  const record = selectedRecord || availableRecord || usedRecord || records[0] || null;

  if (!record) return <div className="raid-tetris-matrix-empty">미입력</div>;
  if (!selectedRecord && !availableRecord) {
    return (
      <div className="raid-tetris-compact-excluded">
        <strong>{usedRecord ? '공격완료 덱 제외' : '사용불가'}</strong>
        <span>{formatEok(record.damageEok)}</span>
      </div>
    );
  }

  return (
    <article className={`raid-tetris-compact-cell ${selectedRecord ? 'selected' : ''}`}>
      <header>
        <b>{formatEok(record.damageEok)}</b>
        {selectedRecord ? <span>추천</span> : records.length > 1 ? <small>최고 딜 슬롯 {record.slotNo}</small> : null}
      </header>
      <RaidPracticeDeckDisplay deck={record.deck} mobile={mobile} />
    </article>
  );
}

export function RaidTetrisBoardView({ mobile = false, tetris }) {
  const season = tetris.selectedSeason;
  const { catalog: optimizationCatalog } = useSharedNikkeCatalog();
  const [displayMode, setDisplayMode] = useState('recommend');
  const [simulationOpen, setSimulationOpen] = useState(false);
  const [fitMode, setFitMode] = useState('nearest');
  const [selectedBossNo, setSelectedBossNo] = useState(1);
  const [selectedAlternativeIndex, setSelectedAlternativeIndex] = useState(0);
  const [targets, setTargets] = useState({});
  const [draftTargets, setDraftTargets] = useState({});
  const [selectedMembers, setSelectedMembers] = useState(() => new Set());
  const [excludedSingleMemberKeys, setExcludedSingleMemberKeys] = useState(() => new Set());
  const [excludedSingleSlotIds, setExcludedSingleSlotIds] = useState(() => new Set());
  const [availabilityPreferences, setAvailabilityPreferences] = useState(readTetrisAvailabilityPreferences);
  const deferredAvailabilityPreferences = useDeferredValue(availabilityPreferences);
  const targetCommitTimers = useRef(new Map());
  const selectionHydration = useRef({ key: '', skipNextSave: false });

  const members = season?.members || [];
  const practiceRecords = season?.practiceRecords || [];
  const actualRecords = season?.records || [];
  const bosses = season?.bosses || [];
  const isInfinityStage = numberValue(season?.globalPhase, 1) >= 4;
  const visibleBosses = isInfinityStage
    ? bosses.filter((boss) => Number(boss.bossNo) === 5)
    : bosses;
  const bossSignature = `${numberValue(season?.globalPhase, 1)}|${visibleBosses.map((boss) => `${boss.bossNo}:${boss.remainingHpEok}`).join('|')}`;

  const actualCountByMember = useMemo(() => {
    const map = new Map();
    for (const record of actualRecords) {
      const openid = memberOpenid(record);
      if (openid) map.set(openid, Math.min(3, (map.get(openid) || 0) + 1));
    }
    return map;
  }, [actualRecords]);

  const nicknameByOpenid = useMemo(() => new Map(members.map((member) => [memberOpenid(member), String(member.nickname || '')])), [members]);
  const normalizedPractice = useMemo(() => practiceRecords
    .map((record) => normalizeTetrisPracticeRecord(record, nicknameByOpenid, optimizationCatalog))
    .sort((a, b) => a.memberOpenid.localeCompare(b.memberOpenid) || a.bossNo - b.bossNo || a.slotNo - b.slotNo), [nicknameByOpenid, optimizationCatalog, practiceRecords]);

  const allCandidates = useMemo(() => {
    const all = buildTetrisCandidates({ members, actualRecords, practiceRecords, ignoreStoredAvailability: true, catalog: optimizationCatalog });
    return isInfinityStage ? all.filter((candidate) => Number(candidate.bossNo) === 5) : all;
  }, [actualRecords, isInfinityStage, members, optimizationCatalog, practiceRecords]);
  const allCandidateSignature = allCandidates.map((candidate) => candidate.id).join('|');
  const allEligibleMemberIds = useMemo(() => [...new Set(allCandidates.map((candidate) => candidate.memberOpenid))], [allCandidates]);

  const baseCandidates = useMemo(() => {
    const all = buildTetrisCandidates({
      members,
      actualRecords,
      practiceRecords,
      preferAvailability: deferredAvailabilityPreferences.preferAvailability,
      currentAvailabilityOnly: deferredAvailabilityPreferences.currentOnly,
      catalog: optimizationCatalog,
    });
    return isInfinityStage ? all.filter((candidate) => Number(candidate.bossNo) === 5) : all;
  }, [actualRecords, deferredAvailabilityPreferences.currentOnly, deferredAvailabilityPreferences.preferAvailability, isInfinityStage, members, optimizationCatalog, practiceRecords]);
  const eligibleMemberIds = useMemo(() => [...new Set(baseCandidates.map((candidate) => candidate.memberOpenid))], [baseCandidates]);
  const selectionStorageKey = candidateSelectionStorageKey(tetris.unionId, season?.seasonNo);

  useEffect(() => {
    if (!season) return;
    const defaults = defaultTargetMap(visibleBosses);
    const stored = loadStoredTargets(targetStorageKey(tetris.unionId, season.seasonNo, season.globalPhase), defaults);
    setTargets(stored);
    setDraftTargets(stored);
    setSelectedBossNo((current) => visibleBosses.some((boss) => Number(boss.bossNo) === Number(current)) ? current : Number(visibleBosses[0]?.bossNo || 1));
    setSelectedAlternativeIndex(0);
  }, [bossSignature, season?.globalPhase, season?.seasonNo, tetris.unionId]);

  useEffect(() => {
    if (!season) return;
    const validMemberKeys = new Set(allCandidates.map((candidate) => `${candidate.bossNo}:${candidate.memberOpenid}`));
    const validSlotIds = new Set(allCandidates.map((candidate) => candidate.id));
    const stored = loadStoredCandidateSelection(selectionStorageKey, validMemberKeys, validSlotIds);
    selectionHydration.current = { key: selectionStorageKey, skipNextSave: true };
    setSelectedMembers(new Set(allEligibleMemberIds));
    setExcludedSingleMemberKeys(stored.excludedMemberKeys);
    setExcludedSingleSlotIds(stored.excludedSlotIds);
  }, [allCandidateSignature, allCandidates, allEligibleMemberIds, season, selectionStorageKey]);

  useEffect(() => {
    const state = selectionHydration.current;
    if (!season || state.key !== selectionStorageKey) return;
    if (state.skipNextSave) {
      state.skipNextSave = false;
      return;
    }
    try {
      window.localStorage.setItem(selectionStorageKey, JSON.stringify({
        excludedMemberKeys: [...excludedSingleMemberKeys],
        excludedSlotIds: [...excludedSingleSlotIds],
      }));
    } catch { /* storage unavailable */ }
  }, [excludedSingleMemberKeys, excludedSingleSlotIds, season, selectionStorageKey]);

  useEffect(() => {
    setSimulationOpen(false);
  }, [season?.globalPhase, season?.seasonNo]);

  useEffect(() => () => {
    for (const timer of targetCommitTimers.current.values()) window.clearTimeout(timer);
    targetCommitTimers.current.clear();
  }, []);

  const globalCandidates = useMemo(() => {
    const all = buildTetrisCandidates({
      members,
      actualRecords,
      practiceRecords,
      selectedMemberIds: selectedMembers,
      preferAvailability: deferredAvailabilityPreferences.preferAvailability,
      currentAvailabilityOnly: deferredAvailabilityPreferences.currentOnly,
      catalog: optimizationCatalog,
    });
    return isInfinityStage ? all.filter((candidate) => Number(candidate.bossNo) === 5) : all;
  }, [actualRecords, deferredAvailabilityPreferences.currentOnly, deferredAvailabilityPreferences.preferAvailability, isInfinityStage, members, optimizationCatalog, practiceRecords, selectedMembers]);

  const singleCandidates = useMemo(() => globalCandidates.filter((candidate) => (
    !excludedSingleMemberKeys.has(`${candidate.bossNo}:${candidate.memberOpenid}`)
    && !excludedSingleSlotIds.has(candidate.id)
  )), [excludedSingleMemberKeys, excludedSingleSlotIds, globalCandidates]);

  const simulationCandidates = useMemo(() => allCandidates.filter((candidate) => (
    !excludedSingleMemberKeys.has(`${candidate.bossNo}:${candidate.memberOpenid}`)
    && !excludedSingleSlotIds.has(candidate.id)
  )), [allCandidates, excludedSingleMemberKeys, excludedSingleSlotIds]);

  const availableIds = useMemo(() => new Set(singleCandidates.map((candidate) => candidate.id)), [singleCandidates]);

  const recommendationsByBoss = useMemo(() => {
    const map = new Map();
    for (const boss of visibleBosses) {
      map.set(Number(boss.bossNo), findBossTetrisCombinations({
        candidates: singleCandidates,
        bossNo: boss.bossNo,
        targetHpEok: targets[boss.bossNo] || 0,
        mode: fitMode,
        topN: 3,
        preferAvailability: deferredAvailabilityPreferences.preferAvailability,
      }));
    }
    return map;
  }, [deferredAvailabilityPreferences.preferAvailability, fitMode, singleCandidates, targets, visibleBosses]);

  const globalPlan = useMemo(() => buildGlobalTetrisPlan({
    bosses: visibleBosses,
    candidates: singleCandidates,
    targets,
    mode: fitMode,
    preferAvailability: deferredAvailabilityPreferences.preferAvailability,
  }), [deferredAvailabilityPreferences.preferAvailability, fitMode, singleCandidates, targets, visibleBosses]);
  const selectedBoss = visibleBosses.find((boss) => Number(boss.bossNo) === Number(selectedBossNo)) || visibleBosses[0] || null;
  const selectedRecommendation = recommendationsByBoss.get(Number(selectedBoss?.bossNo)) || {};
  const selectedRecommendationResult = recommendationResult(selectedRecommendation, selectedAlternativeIndex);
  const selectedRecommendationIds = useMemo(() => new Set(
    (selectedRecommendationResult?.items || []).map((item) => item.id),
  ), [selectedRecommendationResult]);

  useEffect(() => {
    const resultCount = selectedRecommendation.results?.length || 0;
    if (selectedAlternativeIndex >= resultCount) setSelectedAlternativeIndex(0);
  }, [selectedAlternativeIndex, selectedRecommendation]);

  const globalAttackOrderByItem = useMemo(() => {
    const counts = new Map(actualCountByMember);
    const order = new Map();
    for (const boss of visibleBosses) {
      const result = recommendationResult(globalPlan.byBoss.get(Number(boss.bossNo)) || {});
      for (const item of result?.items || []) {
        const attackNo = Math.min(3, (counts.get(item.memberOpenid) || 0) + 1);
        counts.set(item.memberOpenid, attackNo);
        order.set(item.id, attackNo);
      }
    }
    return order;
  }, [actualCountByMember, globalPlan, visibleBosses]);

  const selectedPickIds = useMemo(() => {
    const ids = new Set();
    for (const plan of globalPlan.byBoss.values()) {
      for (const item of plan.selected?.items || []) ids.add(item.id);
    }
    return ids;
  }, [globalPlan]);

  const matrixRecords = useMemo(() => {
    const map = new Map();
    for (const record of normalizedPractice) {
      const key = `${record.memberOpenid}:${record.bossNo}`;
      const list = map.get(key) || [];
      list.push(record);
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((a, b) => b.damageEok - a.damageEok || a.slotNo - b.slotNo);
    return map;
  }, [normalizedPractice]);

  const visibleMembers = useMemo(() => members
    .filter((member) => normalizedPractice.some((record) => record.memberOpenid === memberOpenid(member)))
    .sort((a, b) => {
      const aOpenid = memberOpenid(a);
      const bOpenid = memberOpenid(b);
      const aAvailable = eligibleMemberIds.includes(aOpenid) ? 1 : 0;
      const bAvailable = eligibleMemberIds.includes(bOpenid) ? 1 : 0;
      return bAvailable - aAvailable
        || numberValue(b.syncLevel ?? b.synchroLevel ?? b.level) - numberValue(a.syncLevel ?? a.synchroLevel ?? a.level)
        || String(a.nickname || '').localeCompare(String(b.nickname || ''), 'ko');
    }), [eligibleMemberIds, members, normalizedPractice]);

  const toggleSingleMember = (openid) => {
    if (!selectedBoss) return;
    const stateKey = `${selectedBoss.bossNo}:${openid}`;
    const memberSlots = allCandidates.filter((candidate) => (
      Number(candidate.bossNo) === Number(selectedBoss.bossNo)
      && candidate.memberOpenid === openid
    ));
    setExcludedSingleMemberKeys((current) => {
      const next = new Set(current);
      const turningOn = next.has(stateKey);
      if (turningOn) next.delete(stateKey); else next.add(stateKey);
      if (turningOn) {
        setExcludedSingleSlotIds((slotCurrent) => {
          const slotNext = new Set(slotCurrent);
          memberSlots.forEach((candidate) => slotNext.delete(candidate.id));
          return slotNext;
        });
      }
      return next;
    });
    setSelectedAlternativeIndex(0);
  };

  const toggleSingleSlot = (id, openid) => {
    if (!selectedBoss || excludedSingleMemberKeys.has(`${selectedBoss.bossNo}:${openid}`)) return;
    setExcludedSingleSlotIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setSelectedAlternativeIndex(0);
  };

  const selectAllSelectedBossCandidates = () => {
    if (!selectedBoss) return;
    const bossNo = Number(selectedBoss.bossNo);
    const bossCandidates = allCandidates.filter((candidate) => Number(candidate.bossNo) === bossNo);
    setExcludedSingleMemberKeys((current) => {
      const next = new Set(current);
      bossCandidates.forEach((candidate) => next.delete(`${bossNo}:${candidate.memberOpenid}`));
      return next;
    });
    setExcludedSingleSlotIds((current) => {
      const next = new Set(current);
      bossCandidates.forEach((candidate) => next.delete(candidate.id));
      return next;
    });
    setSelectedAlternativeIndex(0);
  };

  const clearSelectedBossCandidates = () => {
    if (!selectedBoss) return;
    const bossNo = Number(selectedBoss.bossNo);
    const bossCandidates = allCandidates.filter((candidate) => Number(candidate.bossNo) === bossNo);
    setExcludedSingleMemberKeys((current) => {
      const next = new Set(current);
      bossCandidates.forEach((candidate) => next.add(`${bossNo}:${candidate.memberOpenid}`));
      return next;
    });
    setSelectedAlternativeIndex(0);
  };

  const keepOnlyRecommendedMembers = () => {
    if (!selectedBoss) return;
    const bossNo = Number(selectedBoss.bossNo);
    const recommendedMembers = new Set(
      (selectedRecommendationResult?.items || []).map((item) => item.memberOpenid),
    );
    const bossCandidates = allCandidates.filter((candidate) => Number(candidate.bossNo) === bossNo);
    setExcludedSingleMemberKeys((current) => {
      const next = new Set(current);
      bossCandidates.forEach((candidate) => {
        const key = `${bossNo}:${candidate.memberOpenid}`;
        if (recommendedMembers.has(candidate.memberOpenid)) next.delete(key); else next.add(key);
      });
      return next;
    });
    setSelectedAlternativeIndex(0);
  };

  const changeAvailabilityPreference = (key, checked) => {
    const nextChecked = Boolean(checked);
    setAvailabilityPreferences((current) => ({ ...current, [key]: nextChecked }));
    writeTetrisAvailabilityPreference(key, nextChecked);
    setSelectedAlternativeIndex(0);
  };

  const saveTargets = (next) => {
    setTargets(next);
    try { window.localStorage.setItem(targetStorageKey(tetris.unionId, season.seasonNo, season.globalPhase), JSON.stringify(next)); } catch { /* storage unavailable */ }
  };

  const clearTargetTimer = (bossNo) => {
    const timer = targetCommitTimers.current.get(Number(bossNo));
    if (timer) window.clearTimeout(timer);
    targetCommitTimers.current.delete(Number(bossNo));
  };

  const commitTarget = (bossNo, rawValue = draftTargets[bossNo]) => {
    clearTargetTimer(bossNo);
    const normalized = raidHpValueFromInput(rawValue);
    setSelectedAlternativeIndex(0);
    setTargets((current) => {
      const next = { ...current, [bossNo]: normalized };
      try { window.localStorage.setItem(targetStorageKey(tetris.unionId, season.seasonNo, season.globalPhase), JSON.stringify(next)); } catch { /* storage unavailable */ }
      return next;
    });
  };

  const updateTargetDraft = (bossNo, rawValue) => {
    setDraftTargets((current) => ({ ...current, [bossNo]: rawValue }));
    clearTargetTimer(bossNo);
    const timer = window.setTimeout(() => commitTarget(bossNo, rawValue), 350);
    targetCommitTimers.current.set(Number(bossNo), timer);
  };

  const handleTargetKeyDown = (event, bossNo) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    commitTarget(bossNo, event.currentTarget.value);
    event.currentTarget.blur();
  };

  const resetTargets = () => {
    for (const bossNo of targetCommitTimers.current.keys()) clearTargetTimer(bossNo);
    const next = defaultTargetMap(visibleBosses);
    setDraftTargets(next);
    setSelectedAlternativeIndex(0);
    saveTargets(next);
  };

  if (tetris.loading) return <LoadingState label="테트리스 데이터 동기화 중" variant="boss" count={5} />;
  if (tetris.errorMessage) return <section className={`raid-tetris-state error ${mobile ? 'mobile' : ''}`}><strong>테트리스 데이터를 불러오지 못했습니다.</strong><p>{tetris.errorMessage}</p><button type="button" onClick={() => tetris.refresh()}>다시 시도</button></section>;
  if (!season) return <section className={`raid-tetris-state ${mobile ? 'mobile' : ''}`}><strong>표시할 레이드 차수가 없습니다.</strong></section>;

  const selectBoss = (bossNo) => {
    setSelectedBossNo(Number(bossNo));
    setSelectedAlternativeIndex(0);
  };

  const selectedBossSelector = (
    <RaidTetrisCandidateSelector
      mobile={mobile}
      selectedBoss={selectedBoss}
      members={members}
      candidates={baseCandidates}
      excludedMemberKeys={excludedSingleMemberKeys}
      excludedSlotIds={excludedSingleSlotIds}
      recommendedIds={selectedRecommendationIds}
      actualCountByMember={actualCountByMember}
      preferAvailability={availabilityPreferences.preferAvailability}
      currentOnly={availabilityPreferences.currentOnly}
      onChangeAvailabilityPreference={changeAvailabilityPreference}
      onToggleMember={toggleSingleMember}
      onToggleSlot={toggleSingleSlot}
      onSelectAll={selectAllSelectedBossCandidates}
      onClearAll={clearSelectedBossCandidates}
      onKeepRecommended={keepOnlyRecommendedMembers}
    />
  );

  return (
    <section className={`raid-tetris-board ${displayMode}-view ${isInfinityStage ? 'infinity-stage' : ''} ${mobile ? 'mobile' : 'desktop'}`} aria-label="레이드 테트리스">
      <div className="raid-tetris-view-head">
        <h2>테트리스 추천</h2>
        <div className="raid-tetris-view-actions">
          <button className={`shared-view-switch raid-tetris-simulation-switch ${simulationOpen ? 'active' : ''}`} type="button" aria-pressed={simulationOpen} onClick={() => setSimulationOpen((current) => !current)}>
            {simulationOpen ? '시뮬레이션 닫기' : '시뮬레이션'}
          </button>
          <button className="shared-view-switch raid-tetris-view-switch" type="button" aria-pressed={displayMode === 'table'} onClick={() => setDisplayMode((current) => (current === 'recommend' ? 'table' : 'recommend'))}>
            {displayMode === 'recommend' ? '계획표' : '추천'}
          </button>
        </div>
      </div>

      {simulationOpen ? <RaidTetrisFullSimulation season={season} candidates={simulationCandidates} members={members} mobile={mobile} /> : null}

      <section className="raid-tetris-control">
        <div className="raid-tetris-control-copy">
          <h3>{isInfinityStage ? '5보스 인피니티 임의 목표 딜 추천' : '보스별 남은 HP 기준 추천'}</h3>
          <p><strong>{season.seasonNo}차</strong> · 후보 {eligibleMemberIds.length}명 / {singleCandidates.length}슬롯 · {isInfinityStage ? '남은 HP 없음 · 목표 딜을 직접 입력' : '입력 후 0.35초·Enter·포커스 해제 시 재계산'}</p>
        </div>
        <div className="raid-tetris-control-actions">
          <label className="raid-tetris-fit-tabs">
            <span>모드 선택</span>
            <select
              aria-label="추천 모드 선택"
              value={fitMode}
              onChange={(event) => { setFitMode(event.target.value); setSelectedAlternativeIndex(0); }}
            >
              <option value="nearest">오차 최소</option>
              <option value="kill">막타 최소</option>
            </select>
          </label>
          <button type="button" className="raid-tetris-reset-hp" onClick={resetTargets}>{isInfinityStage ? '목표 딜 초기화' : '실전 HP 복원'}</button>
        </div>
      </section>

      {displayMode === 'recommend' ? (
        <>
          <section className={`raid-tetris-boss-targets ${isInfinityStage ? 'infinity-stage' : ''}`}>
            {visibleBosses.map((boss) => {
              const bossNo = Number(boss.bossNo);
              const recommendation = recommendationsByBoss.get(bossNo) || {};
              return (
                <article className={`raid-tetris-boss-target ${bossNo === Number(selectedBossNo) ? 'active' : ''}`} key={bossNo} onClick={() => selectBoss(bossNo)}>
                  <div className="raid-tetris-boss-target-front">
                    <header style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}><strong>{bossDisplayName(boss)}</strong></header>
                    <p>{boss.element || '-'} · {isInfinityStage ? '임의 목표 딜(억)' : '목표 HP(억) · 실전딜 차감 기준'}</p>
                    <div className="raid-tetris-boss-target-input">
                      <input type="number" min="0" step="0.01" value={formatRaidHpInput(draftTargets[bossNo])} onClick={(event) => event.stopPropagation()} onChange={(event) => updateTargetDraft(bossNo, event.target.value)} onBlur={(event) => commitTarget(bossNo, event.target.value)} onKeyDown={(event) => handleTargetKeyDown(event, bossNo)} />
                    </div>
                    <small>{isInfinityStage ? `인피니티 누적 딜 ${formatEok(boss.currentDamageEok)}` : `실전반영 남은 HP ${formatRaidHpEok(boss.remainingHpEok)}`}</small>
                    <small>추천 {recommendationLabel(recommendation, targets[bossNo])}</small>
                  </div>
                </article>
              );
            })}
          </section>

          <section className="raid-tetris-summary-grid">
            <article className="raid-tetris-summary-card selected-boss">
              <header><div><h3>{bossDisplayName(selectedBoss)} 추천</h3><span>{isInfinityStage ? '임의 목표' : '목표'} {formatRaidHpEok(targets[selectedBoss?.bossNo] || 0)}</span></div></header>
              <RecommendationTabs recommendation={selectedRecommendation} selectedIndex={selectedAlternativeIndex} onSelect={setSelectedAlternativeIndex} />
              <RecommendationDecks result={selectedRecommendationResult} target={targets[selectedBoss?.bossNo]} mobile={mobile} />
            </article>

            <article className="raid-tetris-summary-card all-boss">
              <h3>{isInfinityStage ? '5보스 인피니티 계획' : '전체 5보스 계획'}</h3>
              <div className="raid-tetris-global-plan-list">
                {visibleBosses.map((boss) => {
                  const result = recommendationResult(globalPlan.byBoss.get(Number(boss.bossNo)) || {});
                  const target = targets[boss.bossNo] || 0;
                  const diff = result ? numberValue(result.totalEok) - numberValue(target) : 0;
                  return (
                    <article className="raid-tetris-global-plan-row" key={boss.bossNo}>
                      <button type="button" className="raid-tetris-global-plan-boss" onClick={() => selectBoss(boss.bossNo)}>
                        <strong>{bossDisplayName(boss)}</strong>
                      </button>
                      {!target ? <em className="raid-tetris-global-plan-empty">{isInfinityStage ? '임의 목표 딜 없음' : '목표 HP 없음'}</em> : result?.items?.length ? (
                        <div className="raid-tetris-global-plan-body">
                          <div className="raid-tetris-global-plan-items">
                            {result.items.map((item) => (
                              <div className="raid-tetris-global-plan-item" key={item.id}>
                                <span>{globalAttackOrderByItem.get(item.id) || '-'}타</span>
                                <strong>{item.nickname}</strong>
                                <small>{practiceSlotLabel(item)}</small>
                                <b>{formatEok(item.damageEok)}</b>
                              </div>
                            ))}
                          </div>
                          <footer><strong>합계 {formatEok(result.totalEok)}</strong><small>{diff >= 0 ? `초과 +${formatRaidHpEok(diff)}` : `부족 ${formatRaidHpEok(Math.abs(diff))}`} · {result.items.length}타</small></footer>
                        </div>
                      ) : <em className="raid-tetris-global-plan-empty">추천 없음</em>}
                    </article>
                  );
                })}
              </div>
            </article>
          </section>

          {selectedBossSelector}
        </>
      ) : (
        <>
          <div className="raid-tetris-matrix-note">공격완료 3회 멤버와 공격완료 체크된 덱은 자동 제외됩니다. 각 보스 칸에는 전체 배분 추천 슬롯 또는 최고 딜 슬롯 하나만 표시합니다.</div>
          <div className="raid-tetris-matrix-wrap">
            <table className={`raid-tetris-matrix compact-original ${isInfinityStage ? 'infinity-stage' : ''}`}>
              <thead>
                <tr>
                  <th className="member-col" rowSpan="2"><div className="raid-tetris-matrix-corner"><strong>멤버</strong><span>사용 가능 {singleCandidates.length}개 슬롯</span></div></th>
                  {visibleBosses.map((boss) => (
                    <th className={`matrix-boss-head ${Number(selectedBossNo) === Number(boss.bossNo) ? 'active' : ''}`} key={boss.bossNo}>
                      <button type="button" className="raid-tetris-matrix-boss-title" style={{ gridTemplateColumns: 'minmax(0, 1fr) 28px' }} onClick={() => selectBoss(boss.bossNo)}>
                        <strong>{bossDisplayName(boss)}</strong><ElementIcon element={boss.element} />
                      </button>
                      <label><span>{isInfinityStage ? '임의 목표 딜(억)' : '현재 HP(억)'}</span><input type="number" min="0" step="0.01" value={formatRaidHpInput(draftTargets[boss.bossNo])} onChange={(event) => updateTargetDraft(boss.bossNo, event.target.value)} onBlur={(event) => commitTarget(boss.bossNo, event.target.value)} onKeyDown={(event) => handleTargetKeyDown(event, boss.bossNo)} /></label>
                      <small>{isInfinityStage ? `누적 딜 ${formatEok(boss.currentDamageEok)}` : `실전반영 ${formatRaidHpEok(boss.remainingHpEok)}`}</small>
                    </th>
                  ))}
                </tr>
                <tr className="raid-tetris-matrix-rec-row">
                  {visibleBosses.map((boss) => <th key={boss.bossNo}><BossRecommendation recommendation={globalPlan.byBoss.get(Number(boss.bossNo)) || {}} target={targets[boss.bossNo]} /></th>)}
                </tr>
              </thead>
              <tbody>
                {visibleMembers.map((member) => {
                  const openid = memberOpenid(member);
                  const attackCount = actualCountByMember.get(openid) || 0;
                  const eligible = eligibleMemberIds.includes(openid);
                  const memberRecommended = normalizedPractice.some((record) => record.memberOpenid === openid && selectedPickIds.has(record.id));
                  return (
                    <tr className={`${!eligible || !selectedMembers.has(openid) ? 'off' : ''} ${memberRecommended ? 'recommended' : ''}`.trim()} key={openid}>
                      <td className="member-col">
                        <div className="raid-tetris-matrix-member compact">
                          <strong>{member.nickname}</strong>
                          <small>Lv.{numberValue(member.syncLevel ?? member.synchroLevel ?? member.level, '-')}</small>
                          <span>{attackCount >= 3 ? '공격완료' : `남은 ${Math.max(0, 3 - attackCount)}타`}</span>
                        </div>
                      </td>
                      {visibleBosses.map((boss) => {
                        const records = matrixRecords.get(`${openid}:${boss.bossNo}`) || [];
                        return <td className={records.some((record) => selectedPickIds.has(record.id)) ? 'matrix-rec-cell' : ''} key={boss.bossNo}><CompactMatrixCell records={records} selectedIds={selectedPickIds} availableIds={availableIds} memberDone={attackCount >= 3} mobile={mobile} /></td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

export function RaidTetrisBoard({ mobile = false }) {
  const tetris = useRaidTetris();
  return <RaidTetrisBoardView mobile={mobile} tetris={tetris} />;
}
